// router.tsx
import { Navigate, useRouteError } from 'react-router-dom'
import PublicRoutes from './_config/publicRoutes'
import PrivateRoutes from './_config/privateRoutes'
import { APP_ENV, URLS } from './_config'
import { useUserSelector } from './store/selectors/userSelector'
import { lazy, Suspense, useEffect } from 'react'
import Login from './pages/login'
import Loader from './components/loader'
import { publicLoader, authLoader, shouldRevalidateAuth } from './_config/loaders'
import PreExistingTemplates from './pages/PreExistingTemplates'
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7'
import Text from './elements/Typo/Text'
import H2 from './elements/Typo/H2'
import Insight from './pages/insight'

export function withSuspense(element: React.ReactNode) {
    return <Suspense fallback={<Loader />}>{element}</Suspense>
}

const ChunkErrorFallback = () => {
    useEffect(() => {
        // Clear caches and reload
        if ('caches' in globalThis) {
            caches
                .keys()
                .then((names) => {
                    names.forEach((name) => caches.delete(name))
                })
                .finally(() => {
                    globalThis.location.reload()
                })
                .catch((error) => {
                    console.error('Error :', error)
                })
        } else {
            globalThis.location.reload()
        }
    }, [])

    return (
        <div className="flex items-center justify-center min-h-screen bg-white">
            <div className="text-center px-4">
                <div className="mb-8">
                    <Loader />
                </div>

                <H2 className="mb-4 text-[#1E1E1E]">🔄 Updating Application</H2>
                <Text size="lg" className="text-[#464E5F] mb-2">
                    A new version is available. Please wait while we refresh...
                </Text>
                <Text size="sm" className="text-[#7E8299]">
                    This will only take a moment.
                </Text>
            </div>
        </div>
    )
}

const RouterErrorBoundary = () => {
    // const error = useRouteError()
    const error = useRouteError()
    if (error && (error as any).message?.includes('Loading chunk')) {
        return <ChunkErrorFallback />
    }
    // For other errors, re-throw to global handler
    throw error
}

export const useRoutersData = () => {
    const user: any = useUserSelector()

    // SDLC agent pages are dev-only: visible to admins on localhost + the dev build,
    // and hidden in qa/stg/production. NOTE: do not simplify to `APP_ENV === 'dev'` —
    // `start:dev` runs the `default` env block which leaves REACT_APP_ENV unset on
    // localhost, so we exclude-list the deployed envs (mirrors trackEvent in _common/functions.ts).
    const showAgentFeatures = user?.is_admin && APP_ENV !== 'production' && APP_ENV !== 'qa' && APP_ENV !== 'stg'

    const Home = lazy(() => import('./pages/home/index'))
    const Page404 = lazy(() => import('./pages/404'))
    const Profile = lazy(() => import('./pages/profile'))
    const Admin = lazy(() => import('./pages/admin'))
    const Registration = lazy(() => import('./pages/registration'))
    const ForgotPassword = lazy(() => import('./pages/forgotPassword'))
    const ResetPassword = lazy(() => import('./pages/resetPassword'))
    const ConfigureGroups = lazy(() => import('./pages/CreateExperiment/ConfigureGroups'))
    const Layout = lazy(() => import('./pages/layout'))
    const DataOnboarding = lazy(() => import('./pages/DataOnboarding/dataOnboarding'))
    const Glossary = lazy(() => import('./pages/Glossary/glossary'))
    const MyExperiment = lazy(() => import('./pages/MyExperiment'))
    const CreateExperiment = lazy(() => import('./pages/CreateExperiment'))
    const AzureAuth = lazy(() => import('./pages/azureAuth'))
    const InsightReport = lazy(() => import('./pages/ExperimentDetails/InsightReport'))
    const CreateOnBoarding = lazy(() => import('./pages/DataOnboarding/createOnBoarding'))
    const UpdateOnBoarding = lazy(() => import('./pages/DataOnboarding/editOnBoarding'))
    const RegisterApproval = lazy(() => import('./pages/registrationApproval'))
    const Measurement = lazy(() => import('./pages/Measurement'))
    const AdminCreateExperiment = lazy(() => import('./pages/admin/pages/createExperiment'))
    const AdminLiveSimulation = lazy(() => import('./pages/admin/pages/liveSimulation'))
    const AdminMultiTreatment = lazy(() => import('./pages/admin/pages/multiTreatment'))
    const AdminResultTracking = lazy(() => import('./pages/admin/pages/resultTracking'))
    const AdminMyExperiments = lazy(() => import('./pages/admin/pages/myExperiments'))
    const SampleSizeCalculation = lazy(() => import('./pages/SampleSizeCalculation'))
    const ControlCenter = lazy(() => import('./pages/ControlCenter/controlCenter'))
    const DSMethodologyAgent = lazy(() => import('./pages/DSMethodologyAgent'))
    const Agents = lazy(() => import('./pages/Agents'))
    const RequirementAgent = lazy(() => import('./pages/RequirementAgent'))
    const PlanningAgent = lazy(() => import('./pages/PlanningAgent'))
    const DevOpsAgent = lazy(() => import('./pages/DevOpsAgent'))
    const MethodologyAgentIC = lazy(() => import('./pages/MethodologyAgentIC'))
    const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'))
    const ExecutiveDashboardV2 = lazy(() => import('./pages/ExecutiveDashboardV2'))
    const ExecutiveDashboardV3 = lazy(() => import('./pages/ExecutiveDashboardV3'))

    const router = createBrowserRouter([
        {
            path: URLS.HOME,
            id: 'root',
            errorElement: <RouterErrorBoundary />,
            children: [
                {
                    id: 'public',
                    element: <PublicRoutes />,
                    loader: publicLoader,
                    shouldRevalidate: shouldRevalidateAuth,
                    children: [
                        {
                            path: URLS.LOGIN,
                            id: 'login',
                            element: withSuspense(<Login />),
                        },
                        {
                            path: URLS.REGISTER,
                            id: 'register',
                            element: withSuspense(<Registration />),
                        },
                        {
                            path: URLS.REGISTER_APPROVAL,
                            id: 'register-approval',
                            element: withSuspense(<RegisterApproval />),
                        },
                        {
                            path: URLS.FORGOT_PASSWORD,
                            id: 'forgot-password',
                            element: withSuspense(<ForgotPassword />),
                        },
                        {
                            path: URLS.RESET_PASSWORD,
                            id: 'reset-password',
                            element: withSuspense(<ResetPassword />),
                        },
                        {
                            path: URLS.LAYOUT,
                            id: 'layout',
                            element: withSuspense(<Layout />),
                        },
                        {
                            path: URLS.HOME,
                            id: 'home',
                            element: withSuspense(<Home />),
                        },
                    ],
                },

                {
                    id: 'private',
                    element: <PrivateRoutes />,
                    loader: authLoader,
                    shouldRevalidate: shouldRevalidateAuth,
                    children: [
                        {
                            path: URLS.PROFILE,
                            id: 'profile',
                            element: withSuspense(<Profile />),
                        },
                        {
                            path: URLS.MY_EXPERIMENTS,
                            id: 'my-experiments',
                            element: withSuspense(<MyExperiment />),
                        },
                        {
                            path: URLS.DATA_ONBOARDING,
                            id: 'data-onboarding',
                            element: withSuspense(<DataOnboarding />),
                        },
                        {
                            path: URLS.CREATE_ONBOARDING,
                            id: 'create-onboarding',
                            element: withSuspense(<CreateOnBoarding />),
                        },
                        {
                            path: URLS.UPDATE_ONBOARDING(),
                            id: 'update-onboarding',
                            element: withSuspense(<UpdateOnBoarding />),
                        },
                        {
                            path: URLS.GLOSSARY,
                            id: 'glossary',
                            element: withSuspense(<Glossary />),
                        },
                        {
                            path: URLS.GROUP_CONFIGURE,
                            id: 'group-configure',
                            element: withSuspense(<ConfigureGroups />),
                        },
                        {
                            path: URLS.CREATE_EXPERIMENT(),
                            id: 'create-experiment',
                            element: withSuspense(<CreateExperiment />),
                        },
                        {
                            path: URLS.EXPERIMENT_DETAILS(),
                            id: 'experiment-details',
                            element: withSuspense(<Insight />),
                            //loader: experimentDetailsLoader,
                        },
                        {
                            path: URLS.EXPERIMENT_DETAILS_DOWNLOAD(),
                            id: 'experiment-details-download',
                            element: withSuspense(<InsightReport />),
                            //loader: experimentDetailsLoader,
                        },
                        {
                            path: URLS.MEASUREMENT(),
                            id: 'measurement',
                            element: withSuspense(<Measurement />),
                        },
                        {
                            path: URLS.EXECUTIVE_DASHBOARD,
                            id: 'executive-dashboard',
                            element: withSuspense(<ExecutiveDashboardV2 />),
                        },
                        {
                            path: URLS.EXECUTIVE_DASHBOARD_V2,
                            id: 'executive-dashboard-v2',
                            element: withSuspense(<ExecutiveDashboard />),
                        },
                        {
                            path: URLS.EXECUTIVE_DASHBOARD_V3,
                            id: 'executive-dashboard-v3',
                            element: withSuspense(<ExecutiveDashboardV3 />),
                        },
                        {
                            path: URLS.ADMIN_CREATE_EXPERIMENT,
                            id: 'admin-create-experiment',
                            element: withSuspense(<AdminCreateExperiment />),
                        },
                        {
                            path: URLS.ADMIN_LIVE_SIMULATION,
                            id: 'admin-live-simulation',
                            element: withSuspense(<AdminLiveSimulation />),
                        },
                        {
                            path: URLS.ADMIN_MULTI_TREATMENT,
                            id: 'admin-multi-treatment',
                            element: withSuspense(<AdminMultiTreatment />),
                        },
                        {
                            path: URLS.ADMIN_RESULT_TRACKING,
                            id: 'admin-result-tracking',
                            element: withSuspense(<AdminResultTracking />),
                        },
                        {
                            path: URLS.ADMIN_MY_EXPERIMENTS,
                            id: 'admin-my-experiments',
                            element: withSuspense(<AdminMyExperiments />),
                        },
                        {
                            path: URLS.CONTROL_CENTER(),
                            id: 'control-center-all-experiments',
                            element: withSuspense(<ControlCenter />),
                        },
                        {
                            path: URLS.PREEXISTING_TEMPLATE_LIST(),
                            id: 'preexisting-template-list',
                            element: withSuspense(<PreExistingTemplates />),
                        },
                        {
                            path: URLS.PREEXISTING_TEMPLATE_LIST(':templateid'),
                            id: 'preexisting-template-detail',
                            element: withSuspense(<PreExistingTemplates />),
                        },
                        {
                            path: URLS.SAMPLE_SIZE,
                            id: 'sample-size',
                            element: withSuspense(<SampleSizeCalculation />),
                        },
                    ],
                },
                {
                    path: URLS.ADMIN,
                    id: 'admin',
                    element: user?.is_admin ? withSuspense(<Admin />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.DS_AGENT,
                    id: 'ds-agent',
                    element: showAgentFeatures ? withSuspense(<DSMethodologyAgent />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.DS_AGENT_IC,
                    id: 'ds-agent-ic',
                    element: showAgentFeatures ? withSuspense(<MethodologyAgentIC />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.AGENTS,
                    id: 'agents',
                    element: showAgentFeatures ? withSuspense(<Agents />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.REQUIREMENT_AGENT,
                    id: 'requirement-agent',
                    element: showAgentFeatures ? withSuspense(<RequirementAgent />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.PLANNING_AGENT,
                    id: 'planning-agent',
                    element: showAgentFeatures ? withSuspense(<PlanningAgent />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.DEVOPS_AGENT,
                    id: 'devops-agent',
                    element: showAgentFeatures ? withSuspense(<DevOpsAgent />) : <Navigate to={URLS.HOME} />,
                },
                {
                    path: URLS.AZURE_AUTH,
                    id: 'azure-auth',
                    element: withSuspense(<AzureAuth />),
                },

                {
                    path: '*',
                    id: '404',
                    element: withSuspense(<Page404 />),
                },
            ],
        },
    ])

    return { router }
}
